import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { CreditCard, Loader2, Receipt, Repeat } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatMoney } from "@/lib/api";
import { AdminHeader, EmptyState } from "@/pages/admin/AdminLayout";
import { goToCheckout } from "@/components/site/PackagesSection";

const STATUS_CLS = {
  Draft: "border-violet-300/40 bg-violet-300/10 text-violet-200",
  Unpaid: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  Paid: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  Void: "border-slate-400/30 bg-slate-400/10 text-slate-400",
  Active: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  "Past due": "border-red-400/40 bg-red-400/10 text-red-300",
  Cancelled: "border-slate-400/30 bg-slate-400/10 text-slate-400",
  "Not subscribed": "border-cyan-400/30 bg-cyan-400/10 text-cyan-300",
};
export const PayStatus = ({ status, testId }) => <span data-testid={testId} className={`inline-flex w-fit whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_CLS[status] || ""}`}>{status}</span>;
const fmtDate = (d) => (d ? format(parseISO(d), "MMM d, yyyy") : "—");

const PrimaryBtn = ({ onClick, busy, children, testId }) => (
  <button onClick={onClick} disabled={busy} data-testid={testId} className="btn-glow inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">
    {busy && <Loader2 className="h-4 w-4 animate-spin" />} {children}
  </button>
);

const RequestCard = ({ r }) => {
  const [busy, setBusy] = useState(false);
  return (
    <article data-testid={`portal-request-${r.id}`} className="card-tech rounded-2xl p-5 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="font-mono-tech text-[11px] uppercase tracking-widest text-cyan-400">{r.type}</p>
          <h3 className="mt-1 break-words text-lg font-semibold text-white">{r.project_name}</h3>
          <p className="mt-1 text-xs text-slate-500">Due {fmtDate(r.due_date)}</p>
        </div>
        <div className="flex items-center gap-3 sm:flex-col sm:items-end">
          <p data-testid={`portal-request-total-${r.id}`} className="font-display text-2xl font-bold text-white">{formatMoney(r.total)}</p>
          <PayStatus status={r.status} testId={`portal-request-status-${r.id}`} />
        </div>
      </div>
      <ul className="mt-4 space-y-1 border-t border-white/5 pt-4 text-sm">
        {r.line_items.map((li, i) => (
          <li key={i} className="flex justify-between gap-4 text-slate-400"><span className="min-w-0 break-words">{li.description} · {li.quantity} × {formatMoney(li.rate)}</span><span className="shrink-0 text-slate-200">{formatMoney(li.amount)}</span></li>
        ))}
      </ul>
      {r.status === "Unpaid" && <div className="mt-5"><PrimaryBtn busy={busy} testId={`portal-pay-now-${r.id}`} onClick={() => goToCheckout(`/payment-requests/${r.id}/checkout`, setBusy)}><CreditCard className="h-4 w-4" /> Pay now</PrimaryBtn></div>}
    </article>
  );
};

const RetainerCard = ({ r }) => {
  const [busy, setBusy] = useState(false);
  const manage = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/retainers/${r.id}/portal`); window.location.href = data.url; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  const canSubscribe = r.status === "Not subscribed" || r.status === "Cancelled";
  return (
    <article data-testid={`portal-retainer-${r.id}`} className="card-tech flex flex-col gap-4 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
      <div className="min-w-0">
        <p className="font-mono-tech text-[11px] uppercase tracking-widest text-cyan-400">Monthly retainer</p>
        <h3 className="mt-1 break-words text-lg font-semibold text-white">{r.package_name}</h3>
        <p className="mt-1 text-sm text-slate-400">{formatMoney(r.amount)}/month{r.status === "Active" && r.current_period_end && <> · Next billing <span data-testid={`portal-retainer-next-billing-${r.id}`} className="text-slate-200">{fmtDate(r.current_period_end)}</span></>}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <PayStatus status={r.status} testId={`portal-retainer-status-${r.id}`} />
        {canSubscribe ? <PrimaryBtn busy={busy} testId={`portal-subscribe-${r.id}`} onClick={() => goToCheckout(`/retainers/${r.id}/checkout`, setBusy)}><Repeat className="h-4 w-4" /> Subscribe</PrimaryBtn>
          : <button onClick={manage} disabled={busy} data-testid={`portal-manage-subscription-${r.id}`} className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 px-5 py-2.5 text-sm text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Manage subscription</button>}
      </div>
    </article>
  );
};

export default function PortalPayments() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/portal/payments").then((r) => setData(r.data)).catch((e) => { setData({ requests: [], retainers: [] }); toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
  }, []);
  const empty = data && data.requests.length === 0 && data.retainers.length === 0;
  return (
    <div data-testid="portal-payments-page">
      <AdminHeader eyebrow="// billing" title="Payments">Invoices, deposits and monthly plans. Payments are processed securely by Stripe.</AdminHeader>
      {data === null ? <div className="h-40 animate-pulse rounded-2xl bg-white/[0.03]" /> : empty ? (
        <EmptyState icon={Receipt} title="No payments due" testId="portal-payments-empty-state" />
      ) : (
        <div className="space-y-10">
          {data.retainers.length > 0 && <div className="space-y-4"><h2 className="text-lg font-semibold text-slate-200">Monthly plans</h2>{data.retainers.map((r) => <RetainerCard key={r.id} r={r} />)}</div>}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-200">Invoices & deposits</h2>
            {data.requests.length === 0 ? <p data-testid="portal-no-requests" className="text-sm text-slate-500">No payments due</p> : data.requests.map((r) => <RequestCard key={r.id} r={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}
